import { publisherRelease } from "@ondit/publishing";
import {
	Button,
	ChipLink,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	ModalDescription,
} from "@ondit/ui";
import { useState } from "react";
import { useT } from "~/core/i18n";
import { usePublisherExtension } from "./extension";

export function InstallPublisher() {
	const [open, setOpen] = useState(false);
	const t = useT();
	const extension = usePublisherExtension();
	return (
		<>
			<Button variant="ghost" onClick={() => setOpen(true)}>
				{t("publishing.install.title")}
			</Button>
			<PublisherInstallDialog open={open} onOpenChange={setOpen} extension={extension} />
		</>
	);
}

export function PublisherInstallDialog({
	open,
	onOpenChange,
	extension,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	extension: ReturnType<typeof usePublisherExtension>;
}) {
	const t = useT();
	return (
		<ChipModal open={open} onOpenChange={onOpenChange} srTitle={t("publishing.install.title")}>
			<ChipModalHeader onClose={() => onOpenChange(false)} closeAriaLabel={t("common.close")}>
				{t("publishing.install.title")}
			</ChipModalHeader>
			<ChipModalBody>
				<ChipModalField type="custom" title="">
					<p role="status">
						{t(`publishFlow.extension.${extension.status}`)}
						{extension.status === "outdated"
							? ` · ${t("publishFlow.upgradeHelp", { version: publisherRelease.version })}`
							: ""}
					</p>
					<ModalDescription>
						{t("publishing.install.intro")} {t("publishing.install.use")}
					</ModalDescription>
				</ChipModalField>

				{(["chrome", "firefox"] as const).map((browser) => (
					<ChipModalField key={browser} type="custom" title={browser === "chrome" ? "Chrome" : "Firefox"}>
						<ChipLink href={`/generated/publisher/ondit-${browser}.zip`} download>
							{t(`publishing.install.${browser}`)}
						</ChipLink>
						<p className="hint">{t(`publishing.install.${browser}Help`)}</p>
					</ChipModalField>
				))}
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("common.close")}
				onCancel={() => onOpenChange(false)}
				primaryAction={{
					label: t(extension.status === "connected" ? "publishing.install.return" : "publishFlow.recheck"),
					disabled: extension.status === "checking",
					onClick: () => {
						if (extension.status === "connected") onOpenChange(false);
						else extension.check();
					},
				}}
			/>
		</ChipModal>
	);
}

export function PublisherConnection() {
	const extension = usePublisherExtension();
	const t = useT();
	if (extension.status === "connected") return null;
	return (
		<div className="publisher-connection" role="status">
			<div>
				<strong>{t(`publishFlow.extension.${extension.status}`)}</strong>
				{extension.status === "outdated" && (
					<span className="hint"> · {t("publishFlow.upgradeHelp", { version: publisherRelease.version })}</span>
				)}
			</div>
			<div className="platform-actions">
				<InstallPublisher />
				<Button variant="ghost" onClick={extension.check} disabled={extension.status === "checking"}>
					{t("publishFlow.recheck")}
				</Button>
			</div>
		</div>
	);
}
